import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Container,
  Card,
  Fade,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  Zoom,
  Collapse,
  Divider
} from '@mui/material';
import {
  Users,
  DollarSign,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  CheckCircle,
  Sparkles,
  Shield,
  Wifi,
  Globe,
  Tag,
  Smartphone,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { motion } from 'framer-motion';
import { trackSawNumber } from '../../../utils/analytics';
import './DynamicSavings.css';

// Import the Budget Carrier component
import DynamicSavingsBudget from './DynamicSavingsBudget';

// Styled Components
const StyledNumberButton = styled(Button)(({ theme, selected }) => ({
  minWidth: '56px',
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  margin: theme.spacing(0, 0.5),
  backgroundColor: selected ? '#FF4D8D' : 'rgba(255, 77, 141, 0.05)',
  color: selected ? '#fff' : '#444',
  fontWeight: selected ? 'bold' : 'normal',
  fontSize: '1.2rem',
  border: selected ? 'none' : '1px solid rgba(255, 77, 141, 0.15)',
  '&:hover': {
    backgroundColor: selected ? '#FF3377' : 'rgba(255, 77, 141, 0.1)',
    transform: 'translateY(-3px)',
    boxShadow: selected ? '0 8px 20px rgba(255, 77, 141, 0.3)' : '0 4px 10px rgba(255, 77, 141, 0.1)',
  },
  transition: 'all 0.3s ease',
  boxShadow: selected ? '0 6px 15px rgba(255, 77, 141, 0.25)' : 'none',
  [theme.breakpoints.down('sm')]: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    fontSize: '1.1rem',
    margin: theme.spacing(0, 0.25),
  },
  [theme.breakpoints.down('xs')]: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    fontSize: '1rem',
    margin: theme.spacing(0.15),
  }
}));

const SavingsBadge = styled(Box)(({ theme, color = 'pink' }) => ({
  background: color === 'pink' 
    ? 'linear-gradient(135deg, #059669, #10B981)' 
    : 'linear-gradient(135deg, #4F46E5, #818CF8)',
  color: '#fff',
  padding: theme.spacing(1.5, 3),
  borderRadius: '24px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  boxShadow: color === 'pink'
    ? '0 6px 20px rgba(16, 185, 129, 0.25)'
    : '0 6px 20px rgba(79, 70, 229, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 70%)',
    opacity: 0.8,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  '& .MuiTypography-root': {
    color: 'white !important',
  }
}));

const InfoCard = styled(Card)(({ bgcolor = '#FF4D8D', theme }) => ({
  background: `linear-gradient(135deg, ${bgcolor}, ${bgcolor === '#FF4D8D' ? '#FF3377' : '#4F46E5'})`,
  color: '#fff',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '24px',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))',
    opacity: 0.7,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
  },
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15)'
  },
  padding: theme.spacing(3),
  '& .MuiTypography-root': {
    color: 'white',
  }
}));

// Improved table styling for better alignment and readability
const StyledTableRow = styled(TableRow)(({ theme, isPlan, isMvno }) => ({
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': { 
    backgroundColor: isMvno ? 'rgba(79,70,229,0.04)' : 'rgba(255,77,141,0.04)',
    transform: 'scale(1.01)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    zIndex: 10
  },
  '&:hover td': {
    color: isMvno ? '#4F46E5' : '#FF4D8D'
  },
  '&:hover td:first-of-type': {
    borderLeft: isMvno ? '3px solid #4F46E5' : '3px solid #FF4D8D'
  },
  ...(isPlan && {
    backgroundColor: isMvno ? 'rgba(79,70,229,0.04)' : 'rgba(255,77,141,0.04)',
  }),
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(249, 250, 251, 0.5)',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme, align, variant, isMvno }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.95rem',
  borderBottom: '1px solid rgba(0,0,0,0.05)',
  color: variant === 'strikethrough' ? '#666' : (variant === 'savings' ? '#059669' : (variant === 'discount' ? (isMvno ? '#4F46E5' : '#FF4D8D') : '#333')),
  textDecoration: variant === 'strikethrough' ? 'line-through' : 'none',
  textAlign: align || 'left',
  transition: 'all 0.2s ease',
  height: '72px', // Fixed height for consistency
  verticalAlign: 'middle', // Center content vertically
  fontWeight: variant === 'discount' ? 600 : (variant === 'savings' ? 500 : 400),
  
  // Special handling for price columns to ensure consistent alignment
  ...(align === 'right' && {
    paddingRight: theme.spacing(3),
  }),
  
  // Responsive adjustments
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
    fontSize: '0.85rem',
    height: '60px',
  },
}));

// Price cell component to ensure consistent rendering
const PriceCell = ({ children, variant, align = 'right', isMvno, ...props }) => (
  <StyledTableCell 
    variant={variant} 
    align={align} 
    isMvno={isMvno}
    {...props}
  >
    <Typography 
      sx={{ 
        fontWeight: variant === 'discount' ? 600 : 400,
        fontSize: variant === 'discount' ? '1.1rem' : '0.95rem',
        display: 'inline-block',
        width: '100%',
        textAlign: 'right',
        color: variant === 'discount' ? (isMvno ? '#4F46E5' : '#FF4D8D') : 'inherit'
      }}
    >
      {children}
    </Typography>
  </StyledTableCell>
);

// Discount Badge component with improved positioning
const DiscountBadge = ({ percentage, delay, isMvno }) => (
  <Box 
    component={motion.div}
    sx={{
      position: 'absolute',
      top: -4,
      right: 16,
      background: isMvno ? '#4F46E5' : '#FF4D8D',
      color: 'white',
      padding: '2px 8px',
      fontSize: '0.7rem',
      fontWeight: 600,
      borderRadius: '12px',
      boxShadow: isMvno 
        ? '0 4px 8px rgba(79, 70, 229, 0.3)' 
        : '0 4px 8px rgba(255, 77, 141, 0.3)',
      zIndex: 2,
    }}
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ delay: delay + 0.3, duration: 0.4 }}
  >
    {percentage}% off
  </Box>
);

// Animated number counter component
const AnimatedNumber = ({ value, prefix = '', duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  
  useEffect(() => {
    const startValue = 0;
    const endValue = value;
    const step = Math.max(1, Math.floor((endValue - startValue) / (duration / 16)));
    
    let current = startValue;
    let previousTimeStamp = null;
    
    const animate = (timestamp) => {
      if (previousTimeStamp === null) {
        previousTimeStamp = timestamp;
      }
      
      const progress = timestamp - previousTimeStamp;
      
      if (progress > 16) {
        current = Math.min(current + step, endValue);
        setDisplayValue(current);
        previousTimeStamp = timestamp;
      }
      
      if (current < endValue) {
        ref.current = requestAnimationFrame(animate);
      }
    };
    
    ref.current = requestAnimationFrame(animate);
    
    return () => {
      if (ref.current) {
        cancelAnimationFrame(ref.current);
      }
    };
  }, [value, duration]);
  
  return (
    <Typography variant="inherit" component="span">
      {prefix}{displayValue.toLocaleString()}
    </Typography>
  );
};

// Particle Background Component
const ParticleBackground = () => {
  return (
    <Box sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}>
      {[...Array(20)].map((_, i) => (
        <Box
          key={i}
          component={motion.div}
          className="ds-bg-particle"
          sx={{
            position: 'absolute',
            width: Math.random() * 6 + 2,
            height: Math.random() * 6 + 2,
            borderRadius: '50%',
            background: 'rgba(255, 77, 141, 0.2)',
            boxShadow: '0 0 10px 2px rgba(255, 77, 141, 0.1)',
            opacity: Math.random() * 0.5 + 0.3,
          }}
          initial={{
            x: Math.random() * 100 + "%",
            y: Math.random() * 100 + "%",
          }}
          animate={{
            x: [
              Math.random() * 100 + "%",
              Math.random() * 100 + "%",
              Math.random() * 100 + "%",
            ],
            y: [
              Math.random() * 100 + "%",
              Math.random() * 100 + "%",
              Math.random() * 100 + "%",
            ],
          }}
          transition={{
            duration: Math.random() * 20 + 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </Box>
  );
};

// Blob Background Element
const BlobBackground = ({ variant = 'pink' }) => {
  const primaryColor = variant === 'pink' ? '#FF4D8D' : '#4F46E5';
  const secondaryColor = variant === 'pink' ? '#4F46E5' : '#FF4D8D';

  return (
    <Box className="ds-blob ds-blob--primary" sx={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: '600px',
      height: '600px',
      opacity: 0.04,
      zIndex: 0,
      pointerEvents: 'none',
      filter: 'blur(80px)',
      background: `radial-gradient(circle at center, ${primaryColor}, transparent 60%)`,
    }}>
      <Box className="ds-blob ds-blob--secondary" sx={{
        position: 'absolute',
        top: '-200px',
        left: '-200px',
        width: '500px',
        height: '500px',
        opacity: 0.05,
        zIndex: 0,
        filter: 'blur(60px)',
        background: `radial-gradient(circle at center, ${secondaryColor}, transparent 70%)`,
      }}/>
    </Box>
  );
};

// Feature icon with label - improved spacing for readability
const FeatureItem = ({ icon: Icon, text, color = '#4F46E5', bgColor = 'rgba(79, 70, 229, 0.1)' }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
    <Box sx={{ 
      minWidth: 32, 
      height: 32, 
      borderRadius: '50%', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: bgColor,
      mr: 1.5
    }}>
      <Icon size={16} color={color} />
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {text}
    </Typography>
  </Box>
);

// Main Component - DynamicSavings
const DynamicSavings = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState(null); // 'premium' or 'mvno' or null
  const [selectedMembers, setSelectedMembers] = useState(1);
  const [selectedPremiumCarrier, setSelectedPremiumCarrier] = useState('verizon');
  const [selectedPremiumPlan, setSelectedPremiumPlan] = useState('Unlimited Welcome');
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [tableLoaded, setTableLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVisible = true;
  // saw_number should fire once per mount, not on every carrier/plan toggle
  const sawNumberFiredRef = useRef(false);

  // Navigation handler for the View All Circles button
  const handleViewAllCircles = () => {
    navigate('/all-circles');
  };

  // ===== DATA MODELS =====

  // Premium Carrier Plans - UPDATED WITH LATEST PLANS
  const CARRIER_PLANS = {
    verizon: [
      { 
        name: 'Unlimited Welcome', 
        regularPrice: 65, 
        bestGroupPrice: 40, 
        data: 'Unlimited', 
        hotspot: '5GB', 
        priorityData: 'Standard',
        perks: ['6 months Disney+', '6 months Apple Music', 'Verizon Up Rewards', '$500 welcome credit'],
        international: 'Talk & Text to Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 80
      },
      { 
        name: 'Unlimited Plus', 
        regularPrice: 80, 
        bestGroupPrice: 55, 
        data: 'Unlimited', 
        hotspot: '30GB', 
        priorityData: 'Premium (50GB)',
        perks: ['Disney+ included', 'Apple Music 6 months', 'Verizon Up Rewards', '$500 welcome credit', 'Cloud Storage'],
        international: 'Talk, Text, Data in Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 90
      },
      { 
        name: 'Unlimited Ultimate', 
        regularPrice: 90, 
        bestGroupPrice: 65, 
        data: 'Unlimited', 
        hotspot: '60GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['Disney+, Hulu, ESPN+ included', 'Apple Music included', 'Verizon Up Rewards', '$500 welcome credit', '600GB Cloud Storage', 'Apple One 6 months'],
        international: 'Talk, Text, Data in Mexico & Canada + International Day Pass',
        coverage: 'Nationwide 5G Ultra Wideband & 4G LTE',
        valueScore: 98
      }
    ],
    tmobile: [
      { 
        name: 'Essentials', 
        regularPrice: 60, 
        bestGroupPrice: 26.25, 
        data: 'Unlimited', 
        hotspot: '5GB', 
        priorityData: 'Standard',
        perks: ['T-Mobile Tuesdays', '$350 welcome credit', 'Scam Shield'],
        international: 'Talk, Text to Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 75
      },
      { 
        name: 'Go5G', 
        regularPrice: 75, 
        bestGroupPrice: 38.75, 
        data: 'Unlimited', 
        hotspot: '15GB', 
        priorityData: 'Premium (100GB)',
        perks: ['T-Mobile Tuesdays', 'Netflix Basic 1-screen', '$350 welcome credit', 'Scam Shield', 'Free In-flight Wi-Fi', 'Free Texting Worldwide'],
        international: 'Talk, Text, Data in Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 85
      },
      { 
        name: 'Go5G Plus', 
        regularPrice: 90, 
        bestGroupPrice: 46.25, 
        data: 'Unlimited', 
        hotspot: '40GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['T-Mobile Tuesdays', 'Netflix Standard 2-screen', '$350 welcome credit', 'Scam Shield', 'Free In-flight Wi-Fi', 'Free Texting Worldwide', 'Apple TV+ included', '1 year of Paramount+ included'],
        international: 'Talk, Text, Data in Mexico & Canada + 5GB high-speed data in 215+ destinations',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 92
      },
      { 
        name: 'Go5G Next', 
        regularPrice: 100, 
        bestGroupPrice: 56.25, 
        data: 'Unlimited', 
        hotspot: '50GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['T-Mobile Tuesdays', 'Netflix Standard 2-screen', '$350 welcome credit', 'Scam Shield', 'Free In-flight Wi-Fi', 'Free Texting Worldwide', 'Apple TV+ included', '1 year of Paramount+ included', 'Annual phone upgrades'],
        international: 'Talk, Text, Data in Mexico & Canada + 5GB high-speed data in 215+ destinations',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 95
      }
    ],
    att: [
      { 
        name: 'Unlimited Starter SL', 
        regularPrice: 65, 
        bestGroupPrice: 35.99, 
        data: 'Unlimited', 
        hotspot: 'None', 
        priorityData: 'Standard',
        perks: ['$450 welcome credit', 'AT&T ActiveArmor security', '5G Access'],
        international: 'Talk, Text to Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 70
      },
      { 
        name: 'Unlimited Extra EL', 
        regularPrice: 75, 
        bestGroupPrice: 45, 
        data: 'Unlimited', 
        hotspot: '15GB', 
        priorityData: 'Premium (50GB)',
        perks: ['$450 welcome credit', 'AT&T ActiveArmor security', '5G Access', '3 months HBO Max'],
        international: 'Talk, Text, Data in Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 82
      },
      { 
        name: 'Unlimited Premium PL', 
        regularPrice: 85.99, 
        bestGroupPrice: 55, 
        data: 'Unlimited', 
        hotspot: '30GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['HBO Max included', '$450 welcome credit', 'AT&T ActiveArmor security', '5G Access', 'HD Streaming', '100GB Cloud Storage'],
        international: 'Talk, Text, Data in Mexico & Canada + International Day Pass discounts',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 90
      }
    ]
  };

  // Get current premium plan details
  const getCurrentPremiumPlan = () => {
    const plans = CARRIER_PLANS[selectedPremiumCarrier];
    return plans.find(plan => plan.name === selectedPremiumPlan) || plans[0];
  };

  // ===== PREMIUM SAVINGS CALCULATION =====
  
  // Calculate monthly savings for a single premium plan
  const calculateMonthlySavings = () => {
    const plan = getCurrentPremiumPlan();
    return Math.round((plan.regularPrice - plan.bestGroupPrice) * 100) / 100;
  };
  
  // Calculate annual savings for a single premium plan
  const calculateAnnualSavings = () => {
    return calculateMonthlySavings() * 12;
  };
  
  // Get maximum potential savings across all plans
  const getMaxPotentialSavings = () => {
    return calculateAnnualSavings() * selectedMembers;
  };
  
  // Calculate discount percentage
  const getDiscountPercentage = () => {
    const plan = getCurrentPremiumPlan();
    return Math.round(((plan.regularPrice - plan.bestGroupPrice) / plan.regularPrice) * 100);
  };

  // Handle number button click with animation
  const handleNumberButtonClick = (num) => {
    setSelectedMembers(num);
    
    // Re-trigger card animations
    setAnimateCards(false);
    setTimeout(() => {
      setAnimateCards(true);
    }, 100);
  };

  // Premium carrier selection handler
  const handleCarrierSelect = (carrier) => {
    setSelectedPremiumCarrier(carrier);
    setSelectedPremiumPlan(CARRIER_PLANS[carrier][0].name);
  };

  // saw_number: the savings calculator's computed bill renders for this visitor.
  // Fire once per mount, not on every toggle between carriers/plans.
  useEffect(() => {
    if (userType === 'premium' && !sawNumberFiredRef.current) {
      sawNumberFiredRef.current = true;
      trackSawNumber(selectedPremiumCarrier, getCurrentPremiumPlan().bestGroupPrice);
    }
  }, [userType, selectedPremiumCarrier, selectedPremiumPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== COMPONENT ANIMATION INITIALIZATION =====
  
  // Initialize animations
  useEffect(() => {
    // Animate cards after a short delay
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 300);
    
    // Animate table after cards
    const tableTimer = setTimeout(() => {
      setTableLoaded(true);
    }, 600);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(tableTimer);
    };
  }, []);

  return (
    <Box 
      component={motion.div}
      className={`ds-container ${userType === 'mvno' ? 'ds-mvno-container' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      sx={{ 
        background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(249,245,255,1) 100%)',
        position: 'relative',
        pt: 6,
        pb: 8,
        overflow: 'hidden',
      }}
    >
      {/* Render the Budget component when userType is 'mvno' */}
      {userType === 'mvno' ? (
        <DynamicSavingsBudget onBack={() => setUserType(null)} />
      ) : (
        <>
          <ParticleBackground />
          <BlobBackground variant={userType === 'mvno' ? 'indigo' : 'pink'} />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            {/* Title Section */}
            <Box 
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.6 }}
              sx={{ textAlign: 'center', mb: 6 }}
            >
              <Typography 
                variant="h2" 
                component={motion.h2}
                className="ds-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                sx={{ 
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  background: userType === 'mvno' 
                    ? 'linear-gradient(135deg, #4F46E5 0%, #818CF8 100%)'
                    : 'linear-gradient(135deg, #FF4D8D 0%, #FF3377 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textFillColor: 'transparent',
                  fontWeight: 800,
                  mb: 2,
                  letterSpacing: '-0.02em',
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                Calculate Your {userType === 'mvno' ? 'Upgrade Value' : 'Wireless Savings'}
              </Typography>
              
              <Typography 
                variant="h5" 
                component={motion.h5}
                className="ds-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                sx={{ 
                  color: '#333', // Darker for better readability
                  mb: 4,
                  maxWidth: '700px',
                  mx: 'auto',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.6
                }}
              >
                {userType === null && "See how much you can save by joining a circle with friends or new connections"}
                {userType === 'premium' && "See how much you can save by joining a circle with your existing carrier"}
                {userType === 'mvno' && "Discover the premium benefits you've been missing with budget carriers"}
              </Typography>
            </Box>

            {/* Main Calculator Paper */}
            <Paper 
              component={motion.div}
              className="ds-calculator-paper"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3, md: 4 }, 
                borderRadius: { xs: 3, sm: 4, md: 6 },
                background: 'rgba(255, 255, 255, 0.95)', // Increased opacity for better contrast
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                boxShadow: '0 20px 80px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.95) inset',
                position: 'relative',
                overflow: 'hidden',
                zIndex: 2,
              }}
            >
              {/* Paper inner shine effect */}
              <Box className="ds-paper-shine" />

              {/* ===== USER TYPE SELECTION ===== */}
              {!userType && (
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h4" fontWeight="700" sx={{ mb: 2, color: '#222' }}>
                      What's your current situation?
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444' }}> {/* Darker text for better contrast */}
                      Choose the option that best describes your current wireless service
                    </Typography>
                  </Box>

                  <Grid container spacing={4}>
                    {/* Premium Carrier Option */}
                    <Grid item xs={12} md={6}>
                      <Box
                        component={motion.div}
                        whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}
                        sx={{
                          p: { xs: 3, md: 4 },
                          border: '1px solid rgba(255, 77, 141, 0.1)',
                          borderRadius: 4,
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'rgba(255, 77, 141, 0.3)',
                            backgroundColor: 'rgba(255, 77, 141, 0.03)',
                          }
                        }}
                        onClick={() => setUserType('premium')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 77, 141, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <Smartphone size={28} color="#FF4D8D" />
                          </Box>
                          <Typography variant="h5" fontWeight="700" sx={{ color: '#222' }}>
                            Premium Carrier
                          </Typography>
                        </Box>

                        <Typography variant="body1" paragraph sx={{ color: '#444', mb: 3 }}>
                          You're currently on a premium carrier like Verizon, AT&T, or T-Mobile and want to see how much you could save by joining a circle.
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: '#FF4D8D' }}>
                          <Typography fontWeight="600" mr={1}>
                            Calculate Your Savings
                          </Typography>
                          <ArrowRight size={18} />
                        </Box>

                        <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                          <FeatureItem 
                            icon={TrendingDown} 
                            text="Cut your monthly bill by up to 50%" 
                            color="#FF4D8D"
                            bgColor="rgba(255, 77, 141, 0.1)"
                          />
                          <FeatureItem 
                            icon={CheckCircle} 
                            text="Same great premium service & features" 
                            color="#FF4D8D"
                            bgColor="rgba(255, 77, 141, 0.1)"
                          />
                          <FeatureItem 
                            icon={Users} 
                            text="Join a circle with other premium users" 
                            color="#FF4D8D"
                            bgColor="rgba(255, 77, 141, 0.1)"
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {/* MVNO Carrier Option */}
                    <Grid item xs={12} md={6}>
                      <Box
                        component={motion.div}
                        whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}
                        sx={{
                          p: { xs: 3, md: 4 },
                          border: '1px solid rgba(79, 70, 229, 0.1)',
                          borderRadius: 4,
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'rgba(79, 70, 229, 0.3)',
                            backgroundColor: 'rgba(79, 70, 229, 0.03)',
                          }
                        }}
                        onClick={() => setUserType('mvno')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(79, 70, 229, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <Tag size={28} color="#4F46E5" />
                          </Box>
                          <Typography variant="h5" fontWeight="700" sx={{ color: '#222' }}>
                            Budget Carrier
                          </Typography>
                        </Box>

                        <Typography variant="body1" paragraph sx={{ color: '#444', mb: 3 }}>
                          You're currently on a budget carrier like Mint, Cricket, Visible, or Boost and want to see if premium is worth it.
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: '#4F46E5' }}>
                          <Typography fontWeight="600" mr={1}>
                            Compare Total Value
                          </Typography>
                          <ArrowRight size={18} />
                        </Box>

                        <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                          <FeatureItem 
                            icon={Shield} 
                            text="Get premium network priority & coverage" 
                            color="#4F46E5"
                            bgColor="rgba(79, 70, 229, 0.1)"
                          />
                          <FeatureItem 
                            icon={DollarSign} 
                            text="Up to $540 in welcome credits" 
                            color="#4F46E5"
                            bgColor="rgba(79, 70, 229, 0.1)"
                          />
                          <FeatureItem 
                            icon={Sparkles} 
                            text="Premium perks (Netflix, Disney+, etc)" 
                            color="#4F46E5"
                            bgColor="rgba(79, 70, 229, 0.1)"
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ===== PREMIUM SAVINGS CALCULATOR ===== */}
              {userType === 'premium' && (
                <Box>
                  {/* Back Button */}
                  <Button
                    startIcon={<ArrowLeft size={16} />}
                    onClick={() => setUserType(null)}
                    variant="text"
                    sx={{ color: '#555', mb: 3, fontWeight: 500 }}
                  >
                    Back to options
                  </Button>

                  {/* Member Selection */}
                  <Box 
                    component={motion.div}
                    className="ds-group-selector"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    sx={{ 
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      p: { xs: 2, sm: 3, md: 4 }, 
                      borderRadius: 4, 
                      mb: 5,
                      border: '1px solid rgba(255, 77, 141, 0.08)',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.03)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Grid container spacing={3} alignItems="flex-start">
                      <Grid item xs={12} md={5}>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            color: '#222', // Improved contrast
                            mb: 2
                          }}
                        >
                          Select Your Carrier & Plan:
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" sx={{ color: '#444', mb: 1, fontWeight: 500 }}>
                            Your Carrier:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.keys(CARRIER_PLANS).map((carrier) => (
                              <Button 
                                key={carrier}
                                onClick={() => handleCarrierSelect(carrier)}
                                variant={selectedPremiumCarrier === carrier ? "contained" : "outlined"}
                                sx={{ 
                                  textTransform: 'capitalize',
                                  bgcolor: selectedPremiumCarrier === carrier ? '#FF4D8D' : 'transparent',
                                  borderColor: 'rgba(255, 77, 141, 0.3)',
                                  color: selectedPremiumCarrier === carrier ? 'white' : '#333',
                                  fontWeight: selectedPremiumCarrier === carrier ? 600 : 500,
                                  '&:hover': {
                                    bgcolor: selectedPremiumCarrier === carrier ? '#FF3377' : 'rgba(255, 77, 141, 0.05)',
                                    borderColor: 'rgba(255, 77, 141, 0.5)',
                                  },
                                  mb: 1 // Add margin bottom to prevent cramping
                                }}
                              >
                                {carrier}
                              </Button>
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" sx={{ color: '#444', mb: 1, fontWeight: 500 }}>
                            Your Plan:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {CARRIER_PLANS[selectedPremiumCarrier].map((plan) => (
                              <Button 
                                key={plan.name}
                                onClick={() => setSelectedPremiumPlan(plan.name)}
                                variant={selectedPremiumPlan === plan.name ? "contained" : "outlined"}
                                size="small"
                                sx={{ 
                                  bgcolor: selectedPremiumPlan === plan.name ? '#FF4D8D' : 'transparent',
                                  borderColor: 'rgba(255, 77, 141, 0.3)',
                                  color: selectedPremiumPlan === plan.name ? 'white' : '#333',
                                  fontWeight: selectedPremiumPlan === plan.name ? 600 : 500,
                                  '&:hover': {
                                    bgcolor: selectedPremiumPlan === plan.name ? '#FF3377' : 'rgba(255, 77, 141, 0.05)',
                                    borderColor: 'rgba(255, 77, 141, 0.5)',
                                  },
                                  mb: 1 // Add margin bottom to prevent cramping
                                }}
                              >
                                {plan.name}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            color: '#222',
                            mb: 2
                          }}
                        >
                          Number of Lines:
                        </Typography>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', // Allow wrapping for mobile
                            gap: 1, 
                            justifyContent: { xs: 'flex-start', md: 'flex-start' } 
                          }}
                        >
                          {[1, 2, 3, 4, 5].map((num) => (
                            <StyledNumberButton
                              key={num}
                              component={motion.button}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              selected={selectedMembers === num}
                              onClick={() => handleNumberButtonClick(num)}
                              variant="contained"
                              disableElevation
                            >
                              {num}
                            </StyledNumberButton>
                          ))}
                        </Box>
                      </Grid>
                      

                    </Grid>
                  </Box>

                  {/* Info Cards */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    mb: 5
                  }}>
                    <Fade in={animateCards} timeout={800} style={{ transitionDelay: '100ms', flex: 1 }}>
                      <Box sx={{ width: '100%' }}>
                        <InfoCard bgcolor="#FF4D8D">
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            textAlign: 'center'
                          }}>
                            <Box sx={{ position: 'absolute', top: '-10px' }}>
                              <DollarSign size={isMobile ? 24 : 28} />
                            </Box>
                            
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                              Your Monthly Savings
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 3 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Regular Price</Typography>
                                <Typography variant="h6" sx={{ textDecoration: 'line-through', opacity: 0.8 }}>
                                  ${getCurrentPremiumPlan().regularPrice}/mo
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>CircledIn Price</Typography>
                                <Typography variant="h6">
                                  ${getCurrentPremiumPlan().bestGroupPrice}/mo
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)', width: '100%' }}>
                              <Typography variant="body2" sx={{ opacity: 0.8 }}>You Save</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                  ${calculateMonthlySavings()}/mo
                                </Typography>
                                <Box sx={{ 
                                  ml: 2, 
                                  px: 1.5, 
                                  py: 0.5, 
                                  bgcolor: 'rgba(255,255,255,0.2)', 
                                  borderRadius: 5,
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}>
                                  {getDiscountPercentage()}% OFF
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </InfoCard>
                      </Box>
                    </Fade>
                    
                    <Fade in={animateCards} timeout={800} style={{ transitionDelay: '300ms', flex: 1 }}>
                      <Box sx={{ width: '100%' }}>
                        <InfoCard bgcolor="#4F46E5">
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            textAlign: 'center'
                          }}>
                            <Box sx={{ position: 'absolute', top: '-10px' }}>
                              <CheckCircle size={isMobile ? 24 : 28} />
                            </Box>
                            
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                              Annual Savings for {selectedMembers} {selectedMembers === 1 ? 'Line' : 'Lines'}
                            </Typography>
                            
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                              $<AnimatedNumber value={calculateAnnualSavings() * selectedMembers} />
                            </Typography>
                            
                            <Box sx={{ 
                              bgcolor: 'rgba(255,255,255,0.1)', 
                              p: 2, 
                              borderRadius: 2,
                              width: '100%'
                            }}>
                              <Typography variant="body2">
                                That's like getting <strong>{Math.round(12 * getCurrentPremiumPlan().bestGroupPrice / getCurrentPremiumPlan().regularPrice)}</strong> months free each year!
                              </Typography>
                            </Box>
                            
                            {selectedMembers > 1 && (
                              <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
                                ${calculateAnnualSavings()} savings per line × {selectedMembers} lines
                              </Typography>
                            )}
                          </Box>
                        </InfoCard>
                      </Box>
                    </Fade>
                  </Box>

                  {/* Comparison Table */}
                  <Fade in={tableLoaded} timeout={1000}>
                    <Box 
                      sx={{ 
                        borderRadius: 4, 
                        overflow: 'hidden',
                        background: 'rgba(255, 255, 255, 0.95)', // Increased opacity for better contrast
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        mb: 4,
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.03)'
                      }}
                    >
                      <TableContainer className="ds-table-container" sx={{ overflowX: 'auto' }}>
                        <Table className="ds-table" sx={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(249, 250, 251, 0.8)' }}>
                              <TableCell width="20%" sx={{ 
                                fontWeight: 600, 
                                fontSize: '1rem',
                                color: '#222', // Darker for better contrast
                                borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
                              }}>Carrier</TableCell>
                              <TableCell width="30%" sx={{ 
                                fontWeight: 600, 
                                fontSize: '1rem',
                                color: '#222',
                                borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
                              }}>Plan</TableCell>
                              <TableCell width="15%" align="right" sx={{ 
                                fontWeight: 600, 
                                fontSize: '1rem',
                                color: '#222',
                                borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
                              }}>Regular Cost</TableCell>
                              <TableCell width="15%" align="right" sx={{ 
                                fontWeight: 600, 
                                fontSize: '1rem',
                                color: '#222',
                                borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
                              }}>Group Price</TableCell>
                              <TableCell width="20%" align="right" sx={{ 
                                fontWeight: 600, 
                                fontSize: '1rem',
                                color: '#222',
                                borderBottom: '2px solid rgba(0, 0, 0, 0.1)'
                              }}>Annual Savings</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(CARRIER_PLANS).map(([carrier, plans]) =>
                              plans.slice(0, showAllPlans ? plans.length : 1).map((plan, index) => {
                                if (showAllPlans || (carrier === selectedPremiumCarrier && plan.name === selectedPremiumPlan)) {
                                  const annualSavings = (plan.regularPrice - plan.bestGroupPrice) * 12;
                                  const discountPercentage = Math.round(((plan.regularPrice - plan.bestGroupPrice) / plan.regularPrice) * 100);
                                  const delay = (index * 0.1) + 0.2;
                                  const isPlan = carrier === selectedPremiumCarrier && plan.name === selectedPremiumPlan;
                                  
                                  return (
                                    <StyledTableRow
                                      component={motion.tr}
                                      key={`${carrier}-${plan.name}`}
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.4, delay }}
                                      isPlan={isPlan}
                                    >
                                      <StyledTableCell>
                                        <Typography fontWeight="600" className="ds-table-carrier" sx={{ textTransform: 'capitalize', color: '#333' }}>
                                          {carrier}
                                          {isPlan && (
                                            <Box component="span" sx={{ 
                                              ml: 1, 
                                              px: 1, 
                                              py: 0.5, 
                                              bgcolor: 'rgba(255, 77, 141, 0.1)', 
                                              borderRadius: 1,
                                              fontSize: '0.7rem',
                                              color: '#FF4D8D'
                                            }}>
                                              Selected
                                            </Box>
                                          )}
                                        </Typography>
                                      </StyledTableCell>
                                      
                                      <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {plan.name}
                                          <Tooltip 
                                            title={
                                              <Box sx={{ p: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                  {carrier.charAt(0).toUpperCase() + carrier.slice(1)} {plan.name}
                                                </Typography>
                                                <Typography variant="body2">
                                                  • Unlimited talk, text & data
                                                </Typography>
                                                <Typography variant="body2">
                                                  • {plan.hotspot} hotspot
                                                </Typography>
                                                <Typography variant="body2">
                                                  • {plan.priorityData} priority
                                                </Typography>
                                              </Box>
                                            } 
                                            arrow
                                            placement="top"
                                          >
                                            <IconButton size="small" sx={{ color: '#4F46E5' }}>
                                              <HelpCircle size={16} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </StyledTableCell>
                                      
                                      <PriceCell variant="strikethrough">
                                        ${plan.regularPrice}
                                      </PriceCell>
                                      
                                      <StyledTableCell align="right" variant="discount" sx={{ position: 'relative' }}>
                                        <DiscountBadge percentage={discountPercentage} delay={delay} />
                                        <Typography sx={{ 
                                          color: '#FF4D8D', 
                                          fontWeight: 700, 
                                          fontSize: '1.1rem',
                                          textAlign: 'right' 
                                        }} className="ds-table-price--discount">
                                          ${plan.bestGroupPrice}
                                        </Typography>
                                      </StyledTableCell>
                                      
                                      <StyledTableCell align="right" variant="savings" className="ds-table-savings">
                                        <motion.div
                                          initial={{ opacity: 0, scale: 0.8 }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{ delay: delay + 0.4, duration: 0.4 }}
                                          style={{ width: '100%', textAlign: 'right' }}
                                        >
                                          ${(annualSavings * selectedMembers).toLocaleString()}
                                        </motion.div>
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  );
                                }
                                return null;
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* View All Plans Button */}
                      <Box 
                        className="ds-view-plans-button-container"
                        sx={{ py: 2, textAlign: 'center', borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}
                      >
                        <Button
                          onClick={() => setShowAllPlans(!showAllPlans)}
                          endIcon={showAllPlans ? <ChevronUp /> : <ChevronDown />}
                          component={motion.button}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="ds-view-plans-button"
                          sx={{
                            color: '#FF4D8D',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 77, 141, 0.05)'
                            }
                          }}
                        >
                          {showAllPlans ? 'Show Less' : 'Compare All Plans'}
                        </Button>
                      </Box>
                    </Box>
                  </Fade>

                  {/* CTA */}
                  <Box 
                    component={motion.div}
                    className="ds-cta-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    sx={{
                      textAlign: 'center',
                      p: { xs: 3, sm: 4, md: 5 },
                      borderRadius: 4,
                      backgroundImage: 'linear-gradient(135deg, rgba(255, 77, 141, 0.05) 0%, rgba(255, 77, 141, 0.1) 100%)',
                      border: '1px solid rgba(255, 77, 141, 0.1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Typography variant="h3" component="h3" className="ds-cta-title" sx={{ 
                      fontWeight: 800, 
                      color: '#FF4D8D', 
                      mb: 2,
                      fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' } 
                    }}>
                      Ready to Save ${getMaxPotentialSavings().toLocaleString()} This Year?
                    </Typography>
                    
                    <Typography variant="h6" className="ds-cta-subtitle" sx={{ 
                      mb: 4, 
                      color: '#444', 
                      fontWeight: 400,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                    }}>
                      Start saving immediately when you join or create a circle
                    </Typography>
                    
                    <Button
                      variant="contained"
                      size="large"
                      component={motion.button}
                      whileHover={{ scale: 1.05, y: -3 }}
                      whileTap={{ scale: 0.98 }}
                      endIcon={<Sparkles size={20} />}
                      onClick={handleViewAllCircles}
                      className="ds-cta-button"
                      sx={{
                        bgcolor: '#FF4D8D',
                        px: { xs: 3, md: 4 },
                        py: 1.5,
                        borderRadius: 50,
                        fontWeight: 600,
                        boxShadow: '0 10px 20px rgba(255, 77, 141, 0.25)',
                        '&:hover': {
                          bgcolor: '#FF3377',
                        }
                      }}
                    >
                      View All Circles
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Container>
        </>
      )}
    </Box>
  );
};

export default DynamicSavings;